import { useEffect, useState } from "react";
import { ImageIcon, CircleHelp, Maximize2, ChevronLeft, ChevronRight, Video } from "lucide-react";
import axiosInstance from "@/api/axios";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

interface BinMediaViewerProps {
  selectedBin: string[];
  className?: string;
}

interface BinMedia {
  id: number;
  file_url: string;
  description: string;
  uploaded_at: string;
  is_primary: boolean;
  bin_id: string;
  media_type: 'image' | 'video' | 'gif';
  mime_type?: string;
}

export function BinMediaViewer({ selectedBin, className }: BinMediaViewerProps) {
  const { t } = useTranslation();
  const [mediaItems, setMediaItems] = useState<BinMedia[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expandedMedia, setExpandedMedia] = useState<BinMedia | null>(null);
  const [hasAnyMedia, setHasAnyMedia] = useState(false);

  useEffect(() => {
    if (selectedBin.length > 0) {
      fetchBinMedia(selectedBin);
      setCurrentIndex(0);
    } else {
      setMediaItems([]);
      setHasAnyMedia(false);
    }
  }, [selectedBin]);

  const fetchBinMedia = async (binIds: string[]) => {
    try {
      setLoading(true);
      setError(null);

      const responses = await Promise.all(
        binIds.map(binId =>
          axiosInstance.get(`bin-media/?bin_id=${binId}`)
        )
      );

      const allMedia = responses.flatMap(response => {
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          return response.data.data.map((item: any) => ({
            ...item,
            bin_id: response.config.url?.split('bin_id=')[1] || ''
          }));
        }
        return [];
      });

      setMediaItems(allMedia);
      setHasAnyMedia(allMedia.length > 0);
    } catch (error) {
      console.error("Error fetching bin media:", error);
      setError(t("failedToLoadBinMedia"));
      setHasAnyMedia(false);
    } finally {
      setLoading(false);
    }
  };

  const goToPrevious = () => {
    setCurrentIndex(prev => prev === 0 ? mediaItems.length - 1 : prev - 1);
  };

  const goToNext = () => {
    setCurrentIndex(prev => prev === mediaItems.length - 1 ? 0 : prev + 1);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (mediaItems.length <= 1) return;
      if (e.key === "ArrowLeft") goToPrevious();
      else if (e.key === "ArrowRight") goToNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mediaItems.length]);

  const getVisibleDots = () => {
    const maxVisibleDots = 4;
    
    if (mediaItems.length <= maxVisibleDots) {
      return {
        dots: Array.from({ length: mediaItems.length }, (_, i) => i),
        showLeftEllipsis: false,
        showRightEllipsis: false
      };
    }

    let startIndex = Math.max(0, currentIndex - Math.floor(maxVisibleDots / 2));
    let endIndex = Math.min(mediaItems.length - 1, startIndex + maxVisibleDots - 1);

    if (endIndex - startIndex + 1 < maxVisibleDots) {
      startIndex = Math.max(0, endIndex - maxVisibleDots + 1);
    }

    const dots = Array.from({ length: endIndex - startIndex + 1 }, (_, i) => startIndex + i);
    
    return {
      dots,
      showLeftEllipsis: startIndex > 0,
      showRightEllipsis: endIndex < mediaItems.length - 1
    };
  };

  const goToDotIndex = (index: number) => {
    setCurrentIndex(index);
  };

  // FIXED: Force aspect-square on mobile to prevent "shrinked" video look.
  // The 'className' prop usually contains aspect-[16/10] from the parent Page.
  // By adding "aspect-square sm:aspect-[16/10]" at the END, we ensure mobile uses a taller ratio (square)
  // while desktop/tablet snaps back to the standard 16/10 card shape.
  const containerClasses = cn(
    "lg:col-span-2 sm:rounded-lg bg-muted/50 p-4 flex flex-col h-full min-w-0 w-full", 
    className,
    "aspect-square sm:aspect-[16/10]" 
  );

  if (loading) {
    return (
      <div className={containerClasses}>
        <div className="flex justify-between items-center mb-2 shrink-0">
          <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{t("binMedia")}</h4>
          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <CircleHelp className="h-5 w-5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("displaysMediaUploadedForSelectedBins")}</p>
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
        <div className="px-2 pt-4 sm:px-6 sm:pt-6 flex-1 min-h-0 w-full">
          <div className="flex h-full items-center justify-center">
            <div className="text-muted-foreground">{t("loadingMedia")}</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={containerClasses}>
        <div className="flex justify-between items-center mb-2 shrink-0">
          <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{t("binMedia")}</h4>
          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <CircleHelp className="h-5 w-5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("displaysMediaUploadedForSelectedBins")}</p>
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
        <div className="px-2 pt-4 sm:px-6 sm:pt-6 flex-1 min-h-0 w-full">
          <div className="flex h-full items-center justify-center">
            <div className="text-destructive">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (selectedBin.length === 0) {
    return (
      <div className={containerClasses}>
        <div className="flex justify-between items-center mb-2 shrink-0">
          <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{t("binMedia")}</h4>
          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <CircleHelp className="h-5 w-5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("displaysMediaUploadedForSelectedBins")}</p>
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
        <div className="px-2 pt-4 sm:px-6 sm:pt-6 flex-1 min-h-0 w-full">
          <div className="flex h-full items-center justify-center">
            <div className="text-muted-foreground">{t("selectBinsToViewMedia")}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAnyMedia) {
    return null;
  }

  const { dots, showLeftEllipsis, showRightEllipsis } = getVisibleDots();

  return (
    <div className={containerClasses}>
      <div className="flex justify-between items-center mb-2 shrink-0">
        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{t("binMedia")}</h4>
        <TooltipProvider>
          <UITooltip>
            <TooltipTrigger asChild>
              <CircleHelp className="h-5 w-5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("displaysMediaImagesGifsVideosForSelectedBins")}</p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>

      {/* Added min-w-0 to this wrapper as well to ensure containment */}
      <div className="px-2 pt-4 sm:px-6 sm:pt-6 flex-1 min-h-0 w-full min-w-0 relative">
        <div className="relative h-full w-full">
          {mediaItems.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/50 hover:bg-black/70 text-white"
                onClick={goToPrevious}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/50 hover:bg-black/70 text-white"
                onClick={goToNext}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}

          <div className="h-full w-full overflow-hidden rounded-md bg-background">
            <div
              className="h-full w-full flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {mediaItems.map((media) => (
                // Changed to shrink-0 basis-full to properly handle slider sizing
                <div key={`${media.bin_id}-${media.id}`} className="h-full shrink-0 basis-full relative">
                  <div className="border rounded-lg overflow-hidden bg-background group relative h-full w-full flex flex-col">
                    <Dialog>
                      <DialogTrigger asChild>
                        <button
                          className="absolute right-2 top-2 z-10 p-1 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => setExpandedMedia(media)}
                        >
                          <Maximize2 className="h-4 w-4 text-white" />
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 flex items-center justify-center">
                        <DialogTitle className="sr-only">{t("mediaViewer")}</DialogTitle>
                        {media.media_type === 'video' ? (
                          <div className="w-full h-full flex items-center justify-center bg-black">
                            <video
                              controls
                              autoPlay
                              className="max-w-full max-h-[80vh]"
                              onError={(e) => console.error("Video error", e)}
                            >
                              <source
                                src={media.file_url}
                                type={media.mime_type || 'video/mp4'}
                              />
                              {t("yourBrowserDoesNotSupportVideo")}
                            </video>
                          </div>
                        ) : (
                          <img
                            src={media.file_url}
                            alt={media.description || t("noDescription")}
                            className="max-w-full max-h-[80vh] object-contain"
                          />
                        )}
                      </DialogContent>
                    </Dialog>

                    <div className="flex-1 relative min-h-0 w-full overflow-hidden bg-black/5">
                      {media.file_url ? (
                        media.media_type === 'video' ? (
                          <div className="relative w-full h-full">
                            <video
                              controls
                              className="absolute inset-0 w-full h-full object-contain bg-black"
                              onError={(e) => {
                                console.error("Video error", e);
                              }}
                            >
                              <source
                                src={media.file_url}
                                type={media.mime_type || 'video/mp4'}
                              />
                              {t("yourBrowserDoesNotSupportHTML5Video")}
                            </video>
                            {!media.file_url && (
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                <Video className="h-12 w-12 text-gray-400" />
                              </div>
                            )}
                          </div>
                        ) : (
                          <img
                            src={media.file_url}
                            alt={media.description || t("noDescription")}
                            className="w-full h-full object-contain"
                          />
                        )
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          {media.media_type === 'video' ? (
                            <Video className="h-12 w-12 text-gray-400" />
                          ) : (
                            <ImageIcon className="h-12 w-12 text-gray-400" />
                          )}
                        </div>
                      )}
                    </div>
                    <div className="p-3 shrink-0 bg-background border-t">
                      <div className="flex justify-between items-start">
                        <div className="min-w-0 flex-1 mr-2">
                          <p className="text-sm font-medium truncate" title={media.description}>
                            {media.description || t("noDescription")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t("uploaded")}: {new Date(media.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs bg-muted px-2 py-1 rounded whitespace-nowrap">
                            {t("bin")}: {media.bin_id}
                          </span>
                        </div>
                      </div>
                      {media.is_primary && (
                        <span className="inline-block mt-1 px-2 py-1 text-xs bg-primary text-primary-foreground rounded">
                          {t("primary")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* DOT NAVIGATION */}
          {mediaItems.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex justify-center items-center gap-1 z-20 pointer-events-none">
              <div className="bg-black/30 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1 pointer-events-auto">
                {showLeftEllipsis && (
                  <span className="text-xs text-white/80 mx-1">...</span>
                )}
                
                {dots.map((dotIndex) => (
                  <button
                    key={dotIndex}
                    className={`h-2 w-2 rounded-full transition-all duration-200 ease-in-out shadow-sm ${
                      currentIndex === dotIndex 
                        ? 'bg-white scale-125' 
                        : 'bg-white/50 hover:bg-white/80'
                    }`}
                    onClick={() => goToDotIndex(dotIndex)}
                    aria-label={t("goToMedia", { number: dotIndex + 1 })}
                  />
                ))}
                
                {showRightEllipsis && (
                  <span className="text-xs text-white/80 mx-1">...</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}